import { yupResolver } from '@hookform/resolvers/yup'
import AddTaskIcon from '@mui/icons-material/AddTask'
import ApprovalIcon from '@mui/icons-material/Approval'
import TextSnippetIcon from '@mui/icons-material/TextSnippet'
import { LoadingButton } from '@mui/lab'
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { UIAction } from 'app/slices/UI'
import FloorMap from 'components/Event/FloorMap'
import OpenStressMap from 'components/Map'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import axiosClient from 'utils/axiosClient'
import { loadImageProcessToCanvas } from 'utils/imageConvert'
import * as yup from 'yup'
interface FormInput {
  eventId: number
  templateId: number
  name: string
  lat: number
  lng: number
  quizText?: string
  quizAnswers: string
  hint?: string
  answeredImage?: string
  answeredText?: string
}
const schema = yup.object({
  eventId: yup.number().required(),
  lat: yup.number().required(),
  lng: yup.number().required(),
  name: yup.string().required(),
  quizAnswers: yup.string().required(),
  templateId: yup.number().required(),
})
let timer: any
const initStepStyle = {
  value: '',
  color: '#000',
  fontSize: 20,
}
export default function AddCheckpoint() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const inputRef = useRef({} as HTMLInputElement)
  const inputPosValueRef = useRef({} as HTMLInputElement)
  const canvasRef = useRef({} as HTMLCanvasElement)
  const [image, setImage] = useState<HTMLCanvasElement>()
  const { id } = useParams<{ id: string }>()
  const event = useAppSelector((s) =>
    s.Event.list.find((f) => f.id === Number(id))
  )
  const templates = useAppSelector((s) => s.UI.templates).filter((f => f.usable === true))
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      eventId: Number(id),
      templateId: 0,
    },
  })
  const [loading, setLoading] = useState(false)
  const [positionValues, setPositionValues] = useState<
    {
      value: string
      color: string
      fontSize: number
    }[]
  >([])
  const [state, setState] = useState<{
    value: string
    color: string
    fontSize: number
  }>(initStepStyle)
  const [step, setStep] = useState(0)

  const selectedTemplate = useMemo(() => {
    if (getValues('templateId')) {
      const temps = templates.find((f) => f.id === getValues('templateId'))
      if (temps) {
        setValue("quizText",temps.qText)
        setValue("hint",temps.hint)
      }
      return temps
    } else return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getValues('templateId'), templates])
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(
      UIAction.setBreadcrumbs([
        {
          text: 'ホーム',
          url: '/',
        },
        {
          text: 'イベント一覧',
          url: '/event',
        },
        {
          text: 'イベント詳細',
          url: '/event/' + event?.id,
        },
        {
          text: 'チェックポイント登録',
          url: '/event/' + event?.id + '/add-point',
        },
      ])
    )
  }, [dispatch, event?.id, event?.name])
  useEffect(() => {
    const img = new Image()
    if (canvasRef.current && selectedTemplate) {
      const ctx = canvasRef.current.getContext('2d')
      img.src = selectedTemplate.qImage
      img.onload = function () {
        canvasRef.current.width = img.width
        canvasRef.current.height = img.height
        if (ctx) {
          ctx.drawImage(img, 0, 0, 360, (360 * img.height) / img.width)
          selectedTemplate.positions.forEach(({ x, y, size }, i) => {
            if (step === i) {
              ctx.beginPath()
              ctx.lineWidth = 2
              ctx.strokeStyle = '#f00'
              ctx.rect(x - size / 2, y - size / 2, size, size)
              ctx.stroke()
            } else if (!positionValues[i] || !positionValues[i].value) {
              ctx.beginPath()
              ctx.setLineDash([2, 5])
              ctx.strokeStyle = '#00f'
              ctx.rect(x - size / 2, y - size / 2, size, size)
              ctx.stroke()
            }
            if (
              !positionValues[i] ||
              (positionValues[i] && !positionValues[i].value)
            ) {
              ctx.textAlign = 'center'
              ctx.textBaseline = 'middle'
              ctx.font = '16px serif'
              ctx.fillStyle = state.color
              ctx.fillText('S' + String(i + 1), x, y)
            } else {
              ctx.textAlign = 'center'
              ctx.textBaseline = 'middle'
              if (step === i) {
                ctx.font = state.fontSize + 'px serif'
                ctx.fillStyle = state.color
                ctx.fillText(state.value, x, y)
              } else {
                ctx.font = positionValues[i].fontSize + 'px serif'
                ctx.fillStyle = positionValues[i].color
                ctx.fillText(positionValues[i].value, x, y)
              }
            }

            img.remove()
          })
        }
      }
    }
  }, [canvasRef, positionValues, selectedTemplate, state, step])
  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    if (
      positionValues.filter((f) => f.value).length !==
      selectedTemplate?.positions.length
    ) {
      const msg = 'チェックポイントの登録には全項目の入力が必要です。'
      enqueueSnackbar(msg, {
        variant: 'error',
      })
      const area = document.getElementById('position-input')
      if (area) {
        area.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        })
        area.classList.add('bg-red-200')
      }
      return
    }
    await handleCreateCheckpoint(data)
  }

  const handleChangeState = async (
    key: keyof typeof state,
    value: string | number
  ) => {
    await new Promise((rs) => {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        setState({
          ...state,
          [key]: value,
        })
        rs(1)
      }, 200)
    })
  }

  const handleCreateCheckpoint = async (data: FormInput) => {
    try {
      setLoading(true)
      let formData = new FormData()
      if (image)
        await new Promise((rs) => {
          image.toBlob((blob) => {
            if (blob) {
              const ext = blob.type.split('/').pop()
              let file = new File([blob], 'answeredImage.' + ext, {
                type: blob.type,
              })
              formData.append('answeredImage', file)
              rs(1)
            }
          })
        })
      formData.append(
        'data',
        JSON.stringify({
          ...data,
          positionValues,
        })
      )
      console.log(formData)
      await axiosClient.post('/api/events/' + id + '/checkpoint', formData)
      setLoading(false)
      const msg = 'チェックポイントを登録しました。'
      enqueueSnackbar(msg, {
        variant: 'success',
      })
      navigate('/event/' + id)
    } catch (_error) {
      const msg = 'エラーが発生しました。'
      enqueueSnackbar(msg, {
        variant: 'error',
      })
    }
  }

  if (!event) return null
  return (
    <>
      <Grid item xs={12} mt={3}>
        <Typography
          variant="h5"
          component="h2"
          sx={{ borderBottom: 2 }}
          fontWeight="bold"
        >
          {event.name}
        </Typography>
      </Grid>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
        my={2}
      >
        {event.useGPS ? (
          <OpenStressMap
            positions={event.checkpoints}
            addPosition={({ lat, lng }) => {
              setValue('lat', lat)
              setValue('lng', lng)
            }}
            error={'lat' in errors || 'lng' in errors}
          />
        ) : (
          <FloorMap
            floors={event.floors}
            addPosition={({ lat, lng }) => {
              setValue('lat', lat)
              setValue('lng', lng)
            }}
            positions={event.checkpoints}
          />
        )}
        <TextField
          required
          margin="dense"
          label="チェックポイント名称"
          fullWidth
          variant="standard"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <ApprovalIcon color="primary" />
              </InputAdornment>
            ),
          }}
          {...register('name')}
          error={'name' in errors}
        />
        <Autocomplete
          disablePortal
          options={[0, ...templates.map((v) => v.id)]}
          getOptionLabel={(id) => {
            const tmp = templates.find((f) => f.id === id)
            return tmp ? tmp.organizerId ? `[作成テンプレート] ${tmp.name}` : `[提供テンプレート] ${tmp.name}` : ''
          }}
          getOptionDisabled={(id) => id === 0}
          sx={{
            my: 2,
          }}
          fullWidth
          value={getValues('templateId')}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                label={'問題テンプレート選択'}
                required
                variant="standard"
                error={'templateId' in errors}
              />
            )
          }}
          onChange={(_event, newValue) => {
            setState(initStepStyle)
            setStep(0)
            setPositionValues([])
            if (newValue)
              setValue('templateId', newValue, {
                shouldValidate: true,
              })
            else
              setValue('templateId', 0, {
                shouldValidate: true,
              })
          }}
        />
        {selectedTemplate && (
          <>
            <Grid container spacing={1} mt={2}>
              <Grid item xs={12} md={8} display="flex" justifyContent="center">
                <Box
                  component="canvas"
                  border={1}
                  borderColor="#999"
                  ref={canvasRef}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack spacing={2}>
                  <Stack>
                    <label htmlFor="select-color">文字色選択</label>
                    <input
                      id="select-color"
                      type="color"
                      value={state.color}
                      onChange={(e) => {
                        const color = e.target.value
                        handleChangeState('color', color)
                      }}
                    />
                  </Stack>
                  <Stack>
                    <label htmlFor="select-fontSize">文字サイズ選択</label>
                    <input
                      id="select-fontSize"
                      type="range"
                      min={10}
                      max={100}
                      style={{
                        maxWidth: 200,
                      }}
                      value={state.fontSize}
                      onChange={(e) => {
                        const fontSize = e.target.value
                        handleChangeState('fontSize', Number(fontSize))
                      }}
                    />
                  </Stack>
                </Stack>
              </Grid>
              <Grid
                item
                xs={12}
                display="flex"
                justifyContent="center"
                id="position-input"
              >
                <Box maxWidth="sm" my={2}>
                  {step === selectedTemplate.positions.length ? (
                    <Button
                      disabled={step === 0}
                      onClick={() => {
                        setStep(0)
                        setState(positionValues[0])
                      }}
                    >
                      やり直す
                    </Button>
                  ) : (
                    <Stack direction="row">
                      <Button
                        disabled={step === 0}
                        onClick={() => {
                          if (step > 0) {
                            inputPosValueRef.current
                              .querySelector('input')
                              ?.focus()
                            setState(positionValues[step - 1])
                            setStep(step - 1)
                          }
                        }}
                      >
                        前へ
                      </Button>
                      <TextField
                        ref={inputPosValueRef}
                        margin="dense"
                        label={`Slot-${step + 1}`}
                        fullWidth
                        variant="outlined"
                        value={state.value}
                        onFocus={() => {
                          const area = document.getElementById('position-input')
                          if (area) area.classList.remove('bg-red-200')
                        }}
                        onChange={(e) => {
                          setState({
                            ...state,
                            value: e.target.value,
                          })
                        }}
                      />
                      <Button
                        disabled={
                          step >= selectedTemplate.positions.length ||
                          !state.value
                        }
                        onClick={() => {
                          if (step < selectedTemplate.positions.length) {
                            inputPosValueRef.current
                              .querySelector('input')
                              ?.focus()
                            const tmp = [...positionValues]
                            tmp[step] = state
                            setPositionValues(tmp)
                            if (!tmp[step + 1]) {
                              setState({ ...state, value: '' })
                            } else {
                              setState(tmp[step + 1])
                            }
                            setStep(step + 1)
                          }
                        }}
                      >
                        {step === selectedTemplate.positions.length - 1
                          ? '設定'
                          : '次へ'}
                      </Button>
                    </Stack>
                  )}
                </Box>
              </Grid>
            </Grid>
          </>
        )}
        <TextField
          margin="dense"
          label="問題文（任意）"
          fullWidth
          variant="standard"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <TextSnippetIcon color="primary" />
              </InputAdornment>
            ),
          }}
          {...register('quizText')}
          error={'quizText' in errors}
        />
        <TextField
          required
          margin="dense"
          label="解答（カンマ区切りで複数設定可能）"
          fullWidth
          variant="standard"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <TextSnippetIcon color="primary" />
              </InputAdornment>
            ),
          }}
          {...register('quizAnswers')}
          error={'quizAnswers' in errors}
        />
        <TextField
          margin="dense"
          label="ヒント（任意）"
          fullWidth
          multiline
          rows={5}
          sx={{
            mt: 1,
          }}
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <TextSnippetIcon color="primary" />
              </InputAdornment>
            ),
          }}
          {...register('hint')}
          error={'hint' in errors}
        />
        <Typography fontWeight="bold" mt={2}>
          クリア後
        </Typography>
        <Typography>クリア画像（任意）</Typography>

        <Stack my={2} direction="row" spacing={2}>
          <input
            ref={inputRef}
            accept="image/*"
            style={{ display: 'none' }}
            id="upload-file"
            type="file"
            onChange={async (e) => {
              if (e.target.files) {
                setImage(await loadImageProcessToCanvas(e.target.files[0]))
                inputRef.current.value = ''
              }
            }}
          />

          {image ? (
            <Stack sx={{ ml: '0 !important' }} spacing={1}>
              <img
                width={image.width}
                src={image.toDataURL()}
                alt="画像"
                style={{
                  maxWidth: '100%',
                  border: '1px solid #999',
                }}
              ></img>
              <Button
                variant="contained"
                component="span"
                size="small"
                color="warning"
                onClick={() => {
                  inputRef.current.value = ''
                  setImage(undefined)
                }}
                style={{ width: 'max-content', minWidth: 200, marginLeft: 0 }}
              >
                クリア画像を削除
              </Button>
            </Stack>
          ) : (
            <label
              htmlFor="upload-file"
              style={{ width: 'max-content', marginLeft: 0 }}
            >
              <Button
                id="upload-image-btn"
                variant="contained"
                component="span"
                size="small"
                color="secondary"
                sx={{
                  minWidth: 200,
                }}
              >
                クリア画像のアップロード
              </Button>
            </label>
          )}
        </Stack>
        <TextField
          margin="dense"
          label="説明文（任意）"
          fullWidth
          multiline
          rows={5}
          sx={{
            mt: 1,
          }}
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <TextSnippetIcon color="primary" />
              </InputAdornment>
            ),
          }}
          {...register('answeredText')}
          error={'answeredText' in errors}
        />
        <Stack justifyContent="center" direction="row">
          <LoadingButton
            disabled={Object.keys(errors).length > 0}
            variant="contained"
            loading={loading}
            type="submit"
            sx={{
              mt: 4,
              px: 4,
            }}
            color={'primary'}
            loadingPosition="end"
            endIcon={<AddTaskIcon />}
          >
            チェックポイントに設定
          </LoadingButton>
        </Stack>
      </Box>
    </>
  )
}
